import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { Lightbulb, Compass, Factory, Users, Microscope } from 'lucide-react';

export default function Research() {
  const { t } = useTranslation();

  const catiFeatures = [
    { key: 'ip', icon: <Microscope className="w-6 h-6 text-gold" /> },
    { key: 'training', icon: <Compass className="w-6 h-6 text-gold" /> },
    { key: 'incubation', icon: <Lightbulb className="w-6 h-6 text-gold" /> },
    { key: 'patents', icon: <Factory className="w-6 h-6 text-gold" /> },
    { key: 'techtransfer', icon: <Users className="w-6 h-6 text-gold" /> },
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('research_title', 'Recherche & Innovation')}
        description={t('research_subtitle', 'Transformer la recherche en impact concret.')}
        breadcrumbs={[{ label: t('nav_research', 'Research'), href: '/research' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* CATI Section */}
          <section id="cati" className="space-y-10">
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              <div className="flex-1 space-y-8">
                <span className="inline-block px-4 py-1.5 text-[10px] font-black font-source text-navy border-2 border-gold rounded-full tracking-widest uppercase">
                  {t('research_cati_badge', 'CATI Center')}
                </span>
                <h2 className="font-black text-3xl md:text-4xl text-navy uppercase tracking-tight italic">
                  {t('research_cati_title')}
                </h2>
                <div className="prose prose-lg text-gray-600 font-medium leading-relaxed">
                  <p>{t('research_cati_desc1')}</p>
                  <p>{t('research_cati_desc2')}</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
                  {catiFeatures.map((f) => (
                    <div key={f.key} className="flex items-center gap-4 bg-gray-50 p-4 border-l-4 border-gold group hover:bg-navy hover:text-white transition-all duration-300">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        {f.icon}
                      </div>
                      <span className="font-black text-xs uppercase tracking-widest">
                        {t(`research_cati_features_${f.key}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/3 aspect-[3/4] rounded-sm overflow-hidden shadow-2xl border-b-8 border-gold">
                <img src="/images/research/studio-app.jpg" alt="CATI Research Lab" className="w-full h-full object-cover" />
              </div>
            </div>
          </section>

          {/* RAFT Section */}
          <section id="raft" className="bg-navy p-12 md:p-20 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000"></div>
            <div className="relative z-10 text-center space-y-8">
              <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto border-2 border-gold/30">
                <Compass className="w-10 h-10 text-gold" />
              </div>
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic italic">
                {t('research_raft_title', 'Approche RAFT')}
              </h2>
              <p className="text-xl md:text-2xl font-light text-white/80 leading-relaxed max-w-4xl mx-auto italic">
                "{t('research_raft_desc')}"
              </p>
            </div>
          </section>

          {/* Research Areas */}
          <section id="areas" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
               {t('research_areas_title', 'Axes de Recherche')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white border border-gray-100 p-8 shadow-sm hover:shadow-xl transition-all group flex flex-col items-center text-center">
                  <div className="text-gold text-[10px] font-black uppercase tracking-widest mb-6">Area {i}</div>
                  <h3 className="text-lg font-black text-navy group-hover:text-gold transition-colors uppercase leading-tight">
                    {t(`research_area_${i}`)}
                  </h3>
                </div>
              ))}
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
